import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Checkbox, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import DropzoneArea from '../ClientDetails/SubComponents/DropzoneArea';
import XtramModal, {
    XtramBadge,
    XtramButton,
    XtramCheckbox,
    XtramField,
    XtramInput,
    XtramNotice,
    XtramSearch,
    XtramSelect,
    XtramStat,
    XtramTable,
    xtramStyles as styles,
} from '../Widgets/XtramModal/XtramModal';
import ImportExportService, { RivhitBackupImportKind } from '../../Services/importExport.service';
import ClientService from '../../Services/client.service';
import EnumsService from '../../Services/enums.service';
import Constants from '../../assests/Constants/constants';
import { rivhitBackupErrorMessage } from './rivhitBackupErrors';
import RivhitBackupComparison, { comparisonKinds, differenceLabel, differenceTone } from './RivhitBackupComparison';
import { useDirectionByLanguage } from '../../hooks/useDirectionByLanguage';

const ZIP_ACCEPT = { 'application/zip': ['.zip'], 'application/x-zip-compressed': ['.zip'] };

const DOUBLE_ENTRY = 'כפולה';
const SINGLE_ENTRY_PREFIX = 'חד צ';

const Action = {
    ClosingBalance: RivhitBackupImportKind.ClosingBalance,
    Movements: RivhitBackupImportKind.Movements,
    Compare: 'compare',
};

const MATCH_TONES = {
    manual: 'info',
    caseId: 'success',
    vatNumber: 'success',
    clientId: 'success',
    taxNumber: 'success',
    ambiguous: 'warning',
};

/** true for double-entry bookkeeping, false for single-entry, null when the client has none set. */
const isDoubleEntry = (accountManagement) => {
    const value = String(accountManagement ?? '').trim();
    if (value === DOUBLE_ENTRY) return true;
    if (value.startsWith(SINGLE_ENTRY_PREFIX)) return false;
    return null;
};

const clientLabel = (client) => (client ? `${client.name || ''} (${client.caseId})` : '');

const filterClients = (options, { inputValue }) => {
    const term = inputValue.trim().toLowerCase();
    const found = term
        ? options.filter((c) => [c.name, c.caseId].some((v) => String(v ?? '').toLowerCase().includes(term)))
        : options;
    return found.slice(0, 100);
};

const suggestionOf = (row, kind) => row.imports.find((i) => i.kind === kind) ?? null;

/** The imports of a business that the backup has data for, and the comparison alone. */
const actionsOf = (row) => [
    ...(suggestionOf(row, Action.ClosingBalance) ? [Action.ClosingBalance] : []),
    ...(suggestionOf(row, Action.Movements) ? [Action.Movements] : []),
    Action.Compare,
];

/** A business imports the year's movements (ledger or income and expense book), else its closing balance. */
const defaultAction = (row) => {
    const actions = actionsOf(row);
    return [Action.Movements, Action.ClosingBalance].find((a) => actions.includes(a)) ?? Action.Compare;
};

const yearsOf = (imports) => [...new Set(imports
    .filter((i) => i.kind === Action.ClosingBalance || i.kind === Action.Movements)
    .flatMap((i) => (i.years || []).map((y) => y.year)))]
    .sort((a, b) => b - a);

const movementsOfYear = (row) => suggestionOf(row, Action.Movements)?.years?.find((y) => y.year === row.year)?.rowCount ?? 0;

const toEditable = (sc) => ({
    sortCodeId: sc.sortCodeId,
    sortCodeName: sc.sortCodeName ?? '',
    sortCodeCategory: sc.sortCodeGroup ?? null,
    sortCodeVatType: sc.sortCodeVatType == null ? null : String(sc.sortCodeVatType),
    participatesInProfitAndLoss: sc.participatesInProfitAndLoss ?? null,
    vat: sc.vat ?? null,
    rowIds: [],
});

/** What the simulated import takes for a sort code it adds instead of the backup's: the settings chosen for it. */
const toSetting = (sc) => ({
    sortCodeId: sc.sortCodeId,
    sortCodeName: sc.sortCodeName?.trim() || null,
    sortCodeGroup: sc.sortCodeCategory ?? null,
    sortCodeVatType: sc.sortCodeVatType == null ? null : parseInt(sc.sortCodeVatType, 10),
    vat: sc.vat ?? null,
    participatesInProfitAndLoss: sc.participatesInProfitAndLoss ?? null,
});

const isComplete = (sc) => !!sc.sortCodeName?.trim()
    && sc.sortCodeVatType != null
    && sc.participatesInProfitAndLoss != null
    && sc.vat != null;

const palCodeKey = (value) => String(value ?? '')
    .trim()
    .replace(/[\u05F4\u05F3'"`]/g, '"')
    .replace(/\s+/g, ' ')
    .toLowerCase();

const toEditablePalCode = (pc) => ({
    palCodeName: (pc.palCodeName || pc.palCodeId || '').trim(),
    palCodeType: pc.palCodeType ?? null,
    palCodeCategory: null,
    rowIds: [],
});

/** A card the movements post to that the system lacks, with what the backup's card index has of it. */
const toMissingCard = (card, backup) => ({
    cardId: card.cardId,
    cardName: backup?.cardName || card.cardName,
    palCode: backup?.palCode || card.palCode || null,
    accountCaseId: backup?.accountCaseId || null,
    location: backup?.location || null,
    phoneNumber: backup?.phoneNumber || null,
    phoneNumber2: backup?.phoneNumber2 || null,
    deductionRate: backup?.deductionRate ?? null,
    deductionValidity: backup?.deductionValidity ?? null,
    bankCode: backup?.bankCode || null,
    branchCode: backup?.branchCode || null,
    accountNumber: backup?.accountNumber || null,
    rowIds: [],
});

/** Merges what one business is missing into what the others are, by key, keeping the rows that use each. */
const collect = (found, items, keyOf, toItem, rowId) => {
    items.forEach((item) => {
        const key = keyOf(item);
        if (!key) return;
        const current = found.get(key) ?? toItem(item);
        if (!current.rowIds.includes(rowId)) current.rowIds = [...current.rowIds, rowId];
        found.set(key, current);
    });
};

/** The items found now, with what the user already set on those found before. */
const keepEdits = (found, previous, keyOf) => [...found.values()].map((item) => {
    const edited = previous.find((p) => keyOf(p) === keyOf(item));
    return edited ? { ...item, ...edited, rowIds: item.rowIds } : item;
});

let nextId = 0;
const newId = () => {
    nextId += 1;
    return nextId;
};

/**
 * Imports Rivhit backups (zip) – one or many, of one client or of many – and compares each with the client's
 * books. Each business of a backup is matched to a client by its number; double-entry books import their closing
 * balance, single-entry books their income and expense book, and the account cards come along. With caseId the
 * backups go to that client unless the user switches to all clients.
 */
const RivhitBackupImportModal = ({
    open,
    onClose,
    accountantId,
    token,
    caseId = null,
    clientName = '',
    clientAccountManagement = null,
    onImported,
}) => {
    const { t, i18n } = useTranslation();
    const direction = useDirectionByLanguage(i18n);
    const tb = useCallback((key, options) => t(`rivhitBackup.${key}`, options), [t]);

    const [scope, setScope] = useState(caseId ? 'client' : 'all');
    const single = !!caseId && scope === 'client';

    const [tab, setTab] = useState('files');
    const [files, setFiles] = useState([]);
    const [password, setPassword] = useState('');
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState([]);
    const [clients, setClients] = useState([]);
    const [search, setSearch] = useState('');
    const [onlyDifferent, setOnlyDifferent] = useState(false);
    const [withCards, setWithCards] = useState(true);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null);
    const [detailId, setDetailId] = useState(null);

    const [missing, setMissing] = useState([]);
    const [selectedMissing, setSelectedMissing] = useState([]);
    const [bulk, setBulk] = useState({});
    const [groupOptions, setGroupOptions] = useState([]);

    const [missingPalCodes, setMissingPalCodes] = useState([]);
    const [selectedPalCodes, setSelectedPalCodes] = useState([]);
    const [palBulk, setPalBulk] = useState({});
    const [palCodes, setPalCodes] = useState([]);
    const [palCategoryOptions, setPalCategoryOptions] = useState([]);
    const [missingCards, setMissingCards] = useState([]);

    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const selectedRef = useRef(selected);
    selectedRef.current = selected;
    const missingRef = useRef(missing);
    missingRef.current = missing;

    /** The settings of the missing sort codes a business's movements use, which its simulation takes. */
    const settingsOf = (row) => missingRef.current.filter((sc) => sc.rowIds.includes(row.id)).map(toSetting);

    useEffect(() => {
        if (!open || !accountantId) return undefined;
        let cancelled = false;
        ClientService.GetAllClientNames(accountantId, token)
            .then((list) => !cancelled && setClients(Array.isArray(list) ? list : []))
            .catch(() => !cancelled && setClients([]));
        EnumsService.GetSortCodesGroupByAccountantId(accountantId, token)
            .then(async (res) => {
                const data = res?.ok ? await res.json() : [];
                if (!cancelled) setGroupOptions((data || []).map((g) => ({ value: g.sortCodeGroup, label: `${g.sortCodeGroup}` })));
            })
            .catch(() => !cancelled && setGroupOptions([]));
        EnumsService.GetPalCodesByAccountantId(accountantId, token)
            .then(async (res) => {
                const data = res?.ok ? await res.json() : [];
                if (!cancelled) setPalCodes(Array.isArray(data) ? data : []);
            })
            .catch(() => !cancelled && setPalCodes([]));
        EnumsService.GetPalCodeCategoriesByAccountantId(accountantId, token)
            .then(async (res) => {
                const data = res?.ok ? await res.json() : [];
                if (!cancelled) {
                    setPalCategoryOptions((data || []).map((c) => ({ value: c.palCodeCategoryId, label: c.palCodeCategoryId })));
                }
            })
            .catch(() => !cancelled && setPalCategoryOptions([]));
        return () => {
            cancelled = true;
        };
    }, [open, accountantId, token]);

    const vatTypeOptions = useMemo(
        () => Object.entries(Constants.SortCodeVatType).map(([value, name]) => ({ value, label: t(name) })),
        [t]
    );
    const vatOptions = useMemo(() => (Constants.VATPercentage || []).map((v) => ({ value: v, label: `${v}%` })), []);
    const yesNoOptions = useMemo(() => [{ value: true, label: t('Yes') }, { value: false, label: t('No') }], [t]);
    const palCodeTypeOptions = useMemo(() => (Constants.PalCodeType || []).map((type) => ({ value: type, label: type })), []);

    const clearResults = () => {
        setRows([]);
        setSelected([]);
        setMissing([]);
        setSelectedMissing([]);
        setBulk({});
        setMissingPalCodes([]);
        setSelectedPalCodes([]);
        setPalBulk({});
        setMissingCards([]);
        setDetailId(null);
        setFiles((prev) => prev.map((f) => ({ ...f, status: 'pending', error: '', count: 0 })));
        setTab('files');
    };

    const handleClose = () => {
        if (busy) return;
        clearResults();
        setFiles([]);
        setPassword('');
        setSearch('');
        setScope(caseId ? 'client' : 'all');
        onClose?.();
    };

    const changeScope = (next) => {
        if (busy || next === scope) return;
        setScope(next);
        clearResults();
    };

    const updateFile = (id, patch) => setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
    const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

    const handleDrop = (accepted) => {
        setFiles((prev) => {
            const known = new Set(prev.map((f) => `${f.file.name}:${f.file.size}`));
            const added = (accepted || [])
                .filter((file) => !known.has(`${file.name}:${file.size}`))
                .map((file) => ({ id: newId(), file, password: '', status: 'pending', error: '', count: 0 }));
            return [...prev, ...added];
        });
    };

    const removeFile = (id) => {
        const removed = rows.filter((r) => r.fileId === id).map((r) => r.id);
        setFiles((prev) => prev.filter((f) => f.id !== id));
        setRows((prev) => prev.filter((r) => r.fileId !== id));
        setSelected((prev) => prev.filter((rowId) => !removed.includes(rowId)));
        if (removed.includes(detailId)) setDetailId(null);
    };

    const passwordOf = (entry) => entry.password || password;

    /** The businesses of one zip: a row for each of its databases that holds a business. */
    const rowsOf = (entry, databases) => databases
        .filter((db) => db.company)
        .map((db) => {
            const company = db.company;
            const imports = db.imports || [];
            let client = null;
            if (single) {
                client = {
                    caseId,
                    name: clientName,
                    accountManagement: clientAccountManagement
                        ?? (company.caseId === caseId ? company.accountManagement : null),
                };
            } else if (company.caseId) {
                client = { caseId: company.caseId, name: company.clientName, accountManagement: company.accountManagement };
            }
            const years = yearsOf(imports);
            const row = {
                id: newId(),
                fileId: entry.id,
                fileName: entry.file.name,
                database: db.path,
                company,
                imports,
                years,
                year: suggestionOf({ imports }, Action.ClosingBalance)?.year
                    ?? suggestionOf({ imports }, Action.Movements)?.year
                    ?? years[0]
                    ?? null,
                client,
                status: 'idle',
                result: null,
                comparison: null,
                error: '',
                imported: false,
            };
            return { ...row, action: defaultAction(row) };
        });

    const canRun = (row) => !!row.client && row.year != null;

    const requestOf = (row) => {
        const entry = files.find((f) => f.id === row.fileId);
        return {
            caseId: row.client.caseId,
            accountantId,
            file: entry.file,
            password: passwordOf(entry),
            database: row.database,
            year: row.year,
            skipDuplicates: true,
        };
    };

    /** The import a comparison simulates: the one the business is about to have, none once imported. */
    const simulationOf = (row) => (!row.imported && [Action.ClosingBalance, Action.Movements].includes(row.action)
        ? row.action
        : null);

    /**
     * Compares the businesses with the client's books – as the import would leave them when they differ now; a
     * business the system already matches is left out of the import.
     */
    const compareRows = async (targets, { keepAction = false } = {}) => {
        for (const [index, row] of targets.entries()) {
            setProgress({ done: index, total: targets.length, name: row.company.name || row.fileName });
            const sortCodes = settingsOf(row);
            updateRow(row.id, { status: 'running', error: '', simulatedWith: JSON.stringify(sortCodes) });
            try {
                const comparison = await ImportExportService.CompareRivhitBackup(
                    { ...requestOf(row), simulate: simulationOf(row), sortCodes },
                    token
                );
                const accountManagement = comparison.accountManagement ?? row.client.accountManagement;
                const alreadyMatches = !keepAction && !row.imported && comparison.isSame && !comparison.simulated;
                updateRow(row.id, {
                    status: 'done',
                    comparison,
                    client: { ...row.client, accountManagement },
                    action: alreadyMatches || row.imported
                        ? Action.Compare
                        : keepAction ? row.action : defaultAction(row),
                });
                if (alreadyMatches) setSelected((prev) => prev.filter((id) => id !== row.id));
            } catch (err) {
                updateRow(row.id, { status: 'error', error: rivhitBackupErrorMessage(err, t) });
            }
        }
        setProgress(null);
    };

    /** Reads the zips not read yet (or that failed), then compares their businesses with the books as they are. */
    const readFiles = async () => {
        const pending = files.filter((f) => f.status !== 'read');
        if (pending.length === 0) return;
        setBusy(true);
        const added = [];
        for (const [index, entry] of pending.entries()) {
            setProgress({ done: index, total: pending.length, name: entry.file.name });
            updateFile(entry.id, { status: 'reading', error: '' });
            try {
                const databases = await ImportExportService.IdentifyRivhitBackup(
                    { accountantId, file: entry.file, password: passwordOf(entry) },
                    token
                );
                let fileRows = rowsOf(entry, databases || []);
                if (single && fileRows.length > 1) {
                    const own = fileRows.filter((r) => r.company.caseId === caseId);
                    if (own.length > 0) fileRows = own;
                }
                if (fileRows.length === 0) {
                    const failed = (databases || []).find((db) => db.error);
                    updateFile(entry.id, {
                        status: 'error',
                        error: failed ? rivhitBackupErrorMessage({ message: failed.error, status: 400 }, t) : tb('noBusiness'),
                    });
                    continue;
                }
                added.push(...fileRows);
                setRows((prev) => [...prev.filter((r) => r.fileId !== entry.id), ...fileRows]);
                updateFile(entry.id, { status: 'read', count: fileRows.length });
            } catch (err) {
                updateFile(entry.id, { status: 'error', error: rivhitBackupErrorMessage(err, t) });
            }
        }
        setProgress(null);
        const ready = added.filter(canRun);
        setSelected((prev) => [...new Set([...prev, ...ready.map((r) => r.id)])]);
        if (added.length > 0) setTab('businesses');
        await compareRows(ready);
        setBusy(false);
    };

    /** Sets the import of every business not imported yet that the backup has it for. */
    const changeAllActions = (action) => setRows((prev) => prev.map((r) => (r.imported || !actionsOf(r).includes(action)
        ? r
        : { ...r, action, ...(r.comparison?.simulated ? { comparison: null, status: 'idle' } : {}) })));

    const changeClient = (row, client) => {
        const candidate = (row.company.candidates || []).find((c) => c.caseId === client?.caseId);
        const accountManagement = client
            ? candidate?.accountManagement ?? (client.caseId === row.company.caseId ? row.company.accountManagement : null)
            : null;
        const next = client ? { caseId: client.caseId, name: client.name, accountManagement } : null;
        updateRow(row.id, {
            client: next,
            action: defaultAction(row),
            comparison: null,
            result: null,
            status: 'idle',
            error: '',
            imported: false,
        });
        setSelected((prev) => (next && row.year != null
            ? [...new Set([...prev, row.id])]
            : prev.filter((id) => id !== row.id)));
    };

    /* ───────── What the imports are missing ───────── */

    const relevantMissing = useMemo(() => missing.map((sc, index) => ({ sc, index })), [missing]);
    const missingIncomplete = missing.some((sc) => !isComplete(sc));
    const palCodesIncomplete = missingPalCodes.some((pc) => !pc.palCodeType);
    const rowsById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
    const existingPalCodes = useMemo(() => new Set(palCodes.map((pc) => palCodeKey(pc.palCodeId))), [palCodes]);

    /**
     * A missing card comes with the backup's account cards when those of every business that needs it are imported;
     * otherwise the import creates it with the name its movements give it.
     */
    const cardComesWithImport = useCallback((card) => withCards && card.rowIds.every((id) => {
        const row = rowsById.get(id);
        return !!row && !!suggestionOf(row, RivhitBackupImportKind.AccountCards);
    }), [withCards, rowsById]);

    const updateMissing = (index, field, value) =>
        setMissing((prev) => prev.map((sc, i) => (i === index ? { ...sc, [field]: value } : sc)));

    const applyBulk = () => {
        setMissing((prev) => prev.map((sc) => (selectedMissing.includes(sc.sortCodeId) ? { ...sc, ...bulk } : sc)));
        setSelectedMissing([]);
        setBulk({});
    };

    const updatePalCode = (index, field, value) =>
        setMissingPalCodes((prev) => prev.map((pc, i) => (i === index ? { ...pc, [field]: value } : pc)));

    const applyPalBulk = () => {
        setMissingPalCodes((prev) => prev.map((pc) => (selectedPalCodes.includes(pc.palCodeName) ? { ...pc, ...palBulk } : pc)));
        setSelectedPalCodes([]);
        setPalBulk({});
    };

    /**
     * What the imports of the businesses need that the system does not have yet: the sort codes of their
     * movements, the main accounts (pal codes) of their cards and the cards their movements post to.
     */
    const checkRows = async (targets) => {
        const sortCodes = new Map();
        const pals = new Map();
        const cards = new Map();
        for (const [index, row] of targets.entries()) {
            setProgress({ done: index, total: targets.length, name: row.company.name || row.fileName, checking: true });
            const request = requestOf(row);
            const backupCards = new Map();
            if (suggestionOf(row, RivhitBackupImportKind.AccountCards)) {
                const res = await ImportExportService.PreviewRivhitBackup(RivhitBackupImportKind.AccountCards, request, token);
                (res?.accountCards || []).forEach((c) => backupCards.set(String(c.cardId), c));
                const absent = (res?.missingPalCodes || [])
                    .filter((pc) => !existingPalCodes.has(palCodeKey(pc.palCodeName || pc.palCodeId)));
                collect(pals, absent, (pc) => palCodeKey(pc.palCodeName || pc.palCodeId), toEditablePalCode, row.id);
            }
            if (row.action === Action.Movements) {
                const res = await ImportExportService.PreviewRivhitBackup(Action.Movements, request, token);
                collect(sortCodes, res?.preview?.missingSortCodes || [], (sc) => sc.sortCodeId, toEditable, row.id);
                collect(
                    cards,
                    res?.preview?.missingAccountCards || [],
                    (c) => c.cardId,
                    (c) => toMissingCard(c, backupCards.get(String(c.cardId))),
                    row.id
                );
            }
        }
        setProgress(null);
        const found = {
            sortCodes: keepEdits(sortCodes, missing, (sc) => sc.sortCodeId),
            palCodes: keepEdits(pals, missingPalCodes, (pc) => palCodeKey(pc.palCodeName)),
            cards: [...cards.values()],
        };
        setMissing(found.sortCodes);
        setMissingPalCodes(found.palCodes);
        setMissingCards(found.cards);
        return found;
    };

    const createSortCodes = async (codes) => {
        const responses = await Promise.all(codes.map((sc) => EnumsService.AddSortCode(
            accountantId,
            sc.sortCodeName.trim(),
            sc.sortCodeId,
            sc.sortCodeCategory ?? null,
            null,
            null,
            parseInt(sc.sortCodeVatType, 10),
            sc.vat,
            sc.participatesInProfitAndLoss,
            null,
            null,
            0,
            token
        )));
        const failed = responses.filter((r) => !r || !r.ok).length;
        if (failed > 0) throw new Error(`${failed} sort codes failed to create`);
        toast.success(t('MissingSortCodesCreated'));
        missingRef.current = [];
        setMissing([]);
        setSelectedMissing([]);
    };

    /** New main accounts go after the last one of their category, a hundred apart. */
    const createPalCodes = async (codes) => {
        const placed = {};
        const created = codes.map((pc) => {
            const category = pc.palCodeCategory ?? null;
            const last = category
                ? palCodes.filter((p) => p.palCodeCategory === category).reduce((max, p) => Math.max(max, p.orderNumber || 0), 0)
                : 0;
            placed[category] = (placed[category] ?? -1) + 1;
            return {
                palCodeId: pc.palCodeName,
                palCodeType: pc.palCodeType,
                palCodeCategory: category,
                orderNumber: (last > 0 ? last + 100 : 100) + placed[category] * 100,
                accountantId,
            };
        });
        const responses = await Promise.all(created.map((pc) => EnumsService.AddPalCode(pc, token)));
        const failed = responses.filter((r) => !r || !r.ok).length;
        if (failed > 0) throw new Error(`${failed} pal codes failed to create`);
        toast.success(t('BalanceClassificationAddedSuccessfully'));
        setPalCodes((prev) => [...prev, ...created]);
        setMissingPalCodes([]);
        setSelectedPalCodes([]);
    };

    /**
     * Creates in the business's client the cards its movements post to that it still lacks – after the backup's
     * account cards were imported, these are the ones the backup has no card of. Returns how many it created.
     */
    const createMissingCards = async (row, request, known) => {
        const res = await ImportExportService.PreviewRivhitBackup(Action.Movements, request, token);
        const cards = (res?.preview?.missingAccountCards || [])
            .map((card) => known.find((k) => k.cardId === card.cardId) ?? toMissingCard(card));
        const palCodeIdOf = (name) => (name
            ? palCodes.find((p) => palCodeKey(p.palCodeId) === palCodeKey(name))?.palCodeId ?? name
            : null);
        const responses = await Promise.all(cards.map((card) => EnumsService.AddAccountCard(
            row.client.caseId,
            card.cardId,
            card.cardName,
            card.accountCaseId,
            palCodeIdOf(card.palCode),
            token,
            card.phoneNumber,
            card.location,
            null,
            null,
            true,
            null,
            null,
            null,
            card.deductionRate == null ? null : card.deductionValidity,
            card.deductionRate,
            ...Array(11).fill(undefined),
            {
                PhoneNumber2: card.phoneNumber2,
                BankCode: card.bankCode,
                BranchCode: card.branchCode,
                AccountNumber: card.accountNumber,
            }
        )));
        const failed = responses.filter((r) => !r || !r.ok).length;
        if (failed > 0) throw Object.assign(new Error(tb('cardsCreateFailed', { count: failed })), { status: 400 });
        return cards.length;
    };

    /* ───────── Import ───────── */

    const chosenRows = () => rowsRef.current.filter((r) => selectedRef.current.includes(r.id) && canRun(r));
    const importing = (targets) => targets.filter((r) => r.action !== Action.Compare);

    /** Checks the businesses about to be imported, so what they miss shows before the user imports. */
    const checkChosen = async () => {
        const targets = importing(chosenRows());
        if (targets.length === 0) return;
        try {
            await checkRows(targets);
        } catch (err) {
            console.error('Checking the Rivhit backups failed:', err);
            toast.error(rivhitBackupErrorMessage(err, t));
        }
    };

    /** The chosen imports are checked again whenever one changes its action, year or client. */
    const checkKey = rows
        .filter((r) => selected.includes(r.id) && canRun(r) && r.action !== Action.Compare)
        .map((r) => `${r.id}:${r.action}:${r.year}:${r.client.caseId}`)
        .join('|');
    const checkedKey = useRef('');
    useEffect(() => {
        if (busy || checkKey === checkedKey.current) return;
        checkedKey.current = checkKey;
        if (!checkKey) {
            setMissing([]);
            setMissingPalCodes([]);
            setMissingCards([]);
            return;
        }
        setBusy(true);
        checkChosen().finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkKey, busy]);

    /**
     * Imports the chosen businesses one after the other – the account cards, the cards their movements still
     * miss, then the closing balance or the movements – and compares each with the client's books. What they miss
     * is checked first: the user completes the new sort codes and main accounts, which are created before the import.
     */
    const runImport = async () => {
        const targets = chosenRows();
        if (targets.length === 0) return;
        setBusy(true);
        try {
            let found;
            try {
                found = await checkRows(importing(targets));
            } catch (err) {
                toast.error(rivhitBackupErrorMessage(err, t));
                return;
            }
            const unseen = (list, previous, keyOf) => list.some((item) => !previous.some((p) => keyOf(p) === keyOf(item)));
            const palCodesToComplete = found.palCodes.some((pc) => !pc.palCodeType)
                || unseen(found.palCodes, missingPalCodes, (pc) => palCodeKey(pc.palCodeName));
            const sortCodesToComplete = found.sortCodes.some((sc) => !isComplete(sc))
                || unseen(found.sortCodes, missing, (sc) => sc.sortCodeId);
            if (palCodesToComplete) {
                setTab('palCodes');
                toast.warning(tb('palCodesIncomplete'));
                return;
            }
            if (sortCodesToComplete) {
                setTab('missing');
                toast.warning(tb('missingIncomplete'));
                return;
            }
            try {
                if (found.palCodes.length > 0) await createPalCodes(found.palCodes);
                if (found.sortCodes.length > 0) await createSortCodes(found.sortCodes);
            } catch (err) {
                console.error('Creating what the Rivhit backups miss failed:', err);
                toast.error(t('CreateSortCodesFailed'));
                return;
            }

            let succeeded = 0;
            let failed = 0;
            let createdCards = 0;
            for (const [index, row] of targets.entries()) {
                setProgress({ done: index, total: targets.length, name: row.company.name || row.fileName });
                updateRow(row.id, { status: 'running', error: '' });
                const request = requestOf(row);
                const imports = row.action !== Action.Compare;
                let result = row.result;
                try {
                    if (imports) {
                        if (withCards && suggestionOf(row, RivhitBackupImportKind.AccountCards)) {
                            await ImportExportService.ImportRivhitBackup(RivhitBackupImportKind.AccountCards, request, token);
                        }
                        if (row.action === Action.Movements) createdCards += await createMissingCards(row, request, found.cards);
                        result = await ImportExportService.ImportRivhitBackup(row.action, request, token);
                    }
                    const comparison = await ImportExportService.CompareRivhitBackup(request, token);
                    updateRow(row.id, {
                        status: 'done',
                        result,
                        comparison,
                        imported: row.imported || imports,
                        action: Action.Compare,
                        client: { ...row.client, accountManagement: comparison.accountManagement ?? row.client.accountManagement },
                    });
                    if (result?.errorCount > 0) failed += 1;
                    else succeeded += 1;
                } catch (err) {
                    failed += 1;
                    updateRow(row.id, { status: 'error', result, error: rivhitBackupErrorMessage(err, t) });
                }
            }
            setProgress(null);
            setMissingCards([]);
            setTab('businesses');
            if (createdCards > 0) toast.info(tb('cardsCreated', { count: createdCards }));
            const message = tb('done', { succeeded, failed });
            if (failed > 0) toast.warning(message);
            else toast.success(message);
            if (succeeded > 0) onImported?.();
        } finally {
            setProgress(null);
            setBusy(false);
        }
    };

    const runCompare = async () => {
        const targets = chosenRows();
        if (targets.length === 0) return;
        setBusy(true);
        await compareRows(targets, { keepAction: true });
        setBusy(false);
    };

    /**
     * Creates the missing sort codes as set, nothing imported, then compares the businesses that use them again –
     * their simulation now takes the sort codes from the system.
     */
    const createSortCodesOnly = async () => {
        if (missing.length === 0 || missingIncomplete) return;
        const targets = chosenRows().filter((r) => missing.some((sc) => sc.rowIds.includes(r.id)));
        setBusy(true);
        try {
            await createSortCodes(missing);
        } catch (err) {
            console.error('Creating the missing sort codes failed:', err);
            toast.error(t('CreateSortCodesFailed'));
            setBusy(false);
            return;
        }
        await compareRows(targets, { keepAction: true });
        setBusy(false);
        if (targets.length > 0) setTab('comparison');
    };

    /* ───────── Rendering ───────── */

    const clientOptions = useCallback((row) => {
        const candidates = row.company.candidates || [];
        const first = new Set(candidates.map((c) => c.caseId));
        return [...candidates, ...clients.filter((c) => !first.has(c.caseId))];
    }, [clients]);

    const visibleRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return rows.filter((r) => (!onlyDifferent || (r.comparison && !r.comparison.isSame))
            && (!term || [r.fileName, r.company.number, r.company.name, r.client?.name, r.client?.caseId]
                .some((v) => String(v ?? '').toLowerCase().includes(term))));
    }, [rows, search, onlyDifferent]);

    const runnable = rows.filter(canRun);
    const chosen = runnable.filter((r) => selected.includes(r.id));
    const importingClosingBalance = chosen.some((r) => r.action === Action.ClosingBalance);
    const pendingRows = rows.filter((r) => !r.imported);
    const allActionOptions = [Action.Movements, Action.ClosingBalance, Action.Compare]
        .filter((a) => pendingRows.some((r) => actionsOf(r).includes(a)))
        .map((a) => ({ value: a, label: tb(a === Action.Movements ? 'actions.allMovements' : `actions.${a}`) }));
    const allAction = pendingRows.length > 0 && pendingRows.every((r) => r.action === pendingRows[0].action)
        ? pendingRows[0].action
        : null;
    const withoutOpening = chosen.find((r) => r.action === Action.Movements
        && suggestionOf(r, Action.Movements)?.books !== 'singleEntry'
        && r.years.some((y) => y < r.year));
    const compared = rows.filter((r) => r.comparison);
    const different = compared.filter((r) => !r.comparison.isSame);
    const detail = rows.find((r) => r.id === detailId) ?? null;
    const pendingFiles = files.filter((f) => f.status !== 'read').length;
    const rowNames = useMemo(() => new Map(rows.map((r) => [r.id, r.company.name || r.fileName])), [rows]);

    const openDetail = (row) => {
        setDetailId(row.id);
        setTab('comparison');
    };

    const actionLabel = (row, action) => {
        if (action !== Action.Movements) return tb(`actions.${action}`);
        return suggestionOf(row, Action.Movements)?.books === 'singleEntry'
            ? tb('actions.cashBook')
            : tb('actions.movements');
    };

    const renderBookkeeping = (row) => {
        const value = row.client?.accountManagement;
        const double = isDoubleEntry(value);
        if (double === null) return <span className={styles.muted}>{row.client ? tb('unknownBookkeeping') : '—'}</span>;
        return <XtramBadge tone={double ? 'info' : undefined}>{double ? tb('doubleEntry') : tb('singleEntry')}</XtramBadge>;
    };

    const renderMatch = (row) => {
        if (single) {
            return row.company.caseId === caseId
                ? <span className={styles.textSuccess}>{tb('matchesClient')}</span>
                : <span className={styles.textWarning}>{tb('notThisClient')}</span>;
        }
        const type = row.client?.caseId && row.client.caseId !== row.company.caseId ? 'manual' : row.company.matchType || 'none';
        return (
            <Tooltip title={type === 'ambiguous' ? (row.company.candidates || []).map(clientLabel).join(', ') : ''}>
                <span>
                    <XtramBadge tone={MATCH_TONES[type]}>{t(`atzmaitBackup.matchTypes.${type}`)}</XtramBadge>
                </span>
            </Tooltip>
        );
    };

    const renderStatus = (row) => {
        if (row.status === 'running') return <CircularProgress size={16} />;
        if (row.status === 'error') {
            return (
                <Tooltip title={row.error}>
                    <span><XtramBadge tone="error">{tb('failed')}</XtramBadge></span>
                </Tooltip>
            );
        }
        if (row.imported && row.result) {
            return row.result.errorCount > 0 ? (
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{(row.result.errorMessages || []).slice(0, 20).join('\n')}</span>}>
                    <span>
                        <XtramBadge tone="warning">
                            {tb('importedWithErrors', { count: row.result.processedCount, errors: row.result.errorCount })}
                        </XtramBadge>
                    </span>
                </Tooltip>
            ) : (
                <XtramBadge tone="success">{tb('imported', { count: row.result.processedCount })}</XtramBadge>
            );
        }
        return <span className={styles.muted}>{tb('notImported')}</span>;
    };

    const renderComparison = (row) => {
        if (!row.comparison) return <span className={styles.muted}>—</span>;
        return (
            <XtramButton variant="ghost" small onClick={() => openDetail(row)}>
                {row.comparison.simulated && <><XtramBadge tone="info">{tb('afterImport')}</XtramBadge>{' '}</>}
                {comparisonKinds(row.comparison).map((kind) => {
                    const part = row.comparison[kind];
                    return (
                        <span key={kind}>
                            {t(`rivhitBackup.comparison.${kind}Short`)}{' '}
                            <XtramBadge tone={differenceTone(part)}>{differenceLabel(part, t)}</XtramBadge>{' '}
                        </span>
                    );
                })}
            </XtramButton>
        );
    };

    const renderFiles = () => (
        <>
            {caseId && (
                <div className={styles.section}>
                    <span className={styles.fieldLabel}>{tb('scopeLabel')}</span>
                    <div className={styles.toolbarGroup}>
                        <XtramButton variant="ghost" active={scope === 'client'} disabled={busy} onClick={() => changeScope('client')}>
                            {tb('scopeClient', { client: clientName || caseId })}
                        </XtramButton>
                        <XtramButton variant="ghost" active={scope === 'all'} disabled={busy} onClick={() => changeScope('all')}>
                            {tb('scopeAll')}
                        </XtramButton>
                    </div>
                </div>
            )}
            <div className={styles.section}>
                <p className={styles.muted}>{single ? tb('help') : tb('helpAll')}</p>
                <DropzoneArea onDrop={handleDrop} accept={ZIP_ACCEPT} multiple />
            </div>
            <div className={styles.section}>
                <div className={styles.formFields}>
                    <XtramField label={tb('password')} hint={tb('passwordHelp')} size="wide">
                        <XtramInput
                            direction={direction}
                            type="password"
                            autoComplete="off"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </XtramField>
                </div>
            </div>
            {files.length > 0 && (
                <XtramTable height="medium">
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{tb('file')}</TableCell>
                                <TableCell>{tb('filePassword')}</TableCell>
                                <TableCell>{tb('fileStatus')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {files.map((f) => (
                                <TableRow key={f.id} className={f.status === 'error' ? styles.rowError : undefined}>
                                    <TableCell>{f.file.name}</TableCell>
                                    <TableCell>
                                        <div className={styles.cellField}>
                                            <XtramInput
                                                direction={direction}
                                                type="password"
                                                autoComplete="off"
                                                placeholder={tb('sharedPassword')}
                                                value={f.password}
                                                disabled={busy}
                                                onChange={(e) => updateFile(f.id, { password: e.target.value, status: f.status === 'read' ? 'read' : 'pending' })}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {f.status === 'reading' && <CircularProgress size={16} />}
                                        {f.status === 'pending' && <XtramBadge>{tb('fileStatuses.pending')}</XtramBadge>}
                                        {f.status === 'read' && <XtramBadge tone="success">{tb('fileStatuses.read', { count: f.count })}</XtramBadge>}
                                        {f.status === 'error' && <span className={styles.textError}>{f.error}</span>}
                                    </TableCell>
                                    <TableCell>
                                        <XtramButton variant="ghost" small disabled={busy} onClick={() => removeFile(f.id)}>
                                            {tb('remove')}
                                        </XtramButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </XtramTable>
            )}
        </>
    );

    const renderBusinesses = () => {
        const allChosen = runnable.length > 0 && runnable.every((r) => selected.includes(r.id));
        return (
            <div className={styles.section}>
                <div className={styles.stats}>
                    <XtramStat label={tb('businessCount')} value={rows.length} />
                    {!single && <XtramStat label={tb('matchedCount')} value={rows.filter((r) => r.client).length} tone="success" />}
                    <XtramStat label={tb('sameCount')} value={compared.length - different.length} tone="success" />
                    <XtramStat
                        label={tb('differentCount')}
                        value={different.length}
                        tone={different.length > 0 ? 'error' : undefined}
                        onClick={different.length > 0 ? () => setOnlyDifferent((v) => !v) : undefined}
                        active={onlyDifferent}
                    />
                </div>
                <XtramNotice type="info">{tb('booksHelp')}</XtramNotice>
                {importingClosingBalance && <XtramNotice type="warning">{tb('closingBalanceWarning')}</XtramNotice>}
                {withoutOpening && (
                    <XtramNotice type="warning">{tb('openingBalancesHelp', { year: withoutOpening.year - 1 })}</XtramNotice>
                )}
                <div className={styles.toolbar}>
                    {rows.length > 5 && (
                        <XtramSearch direction={direction} value={search} onChange={setSearch} placeholder={tb('search')} />
                    )}
                    <div className={styles.cellField}>
                        <XtramField label={tb('allActions')}>
                            <XtramSelect
                                direction={direction}
                                options={allActionOptions}
                                value={allActionOptions.find((o) => o.value === allAction) ?? null}
                                onChange={(o) => o && changeAllActions(o.value)}
                                disabled={busy || pendingRows.length === 0}
                            />
                        </XtramField>
                    </div>
                    <XtramCheckbox checked={withCards} onChange={setWithCards} label={tb('withCards')} disabled={busy} />
                </div>
                <XtramTable height="tall">
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        className={styles.tableCheckbox}
                                        disabled={busy || runnable.length === 0}
                                        checked={allChosen}
                                        indeterminate={!allChosen && chosen.length > 0}
                                        onChange={(e) => setSelected(e.target.checked ? runnable.map((r) => r.id) : [])}
                                    />
                                </TableCell>
                                <TableCell>{tb('business')}</TableCell>
                                {!single && <TableCell>{tb('client')}</TableCell>}
                                <TableCell>{tb('bookkeeping')}</TableCell>
                                <TableCell>{tb('year')}</TableCell>
                                <TableCell>{tb('action')}</TableCell>
                                <TableCell>{tb('status')}</TableCell>
                                <TableCell>{tb('comparisonColumn')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} className={styles.emptyState}>{t('importModal.noResults')}</TableCell>
                                </TableRow>
                            )}
                            {visibleRows.map((row) => {
                                const yearOptions = row.years.map((y) => ({ value: y, label: String(y) }));
                                const actionOptions = actionsOf(row).map((a) => ({ value: a, label: actionLabel(row, a) }));
                                const rowCount = movementsOfYear(row);
                                return (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        className={row.comparison && !row.comparison.isSame ? styles.rowWarning : undefined}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                className={styles.tableCheckbox}
                                                disabled={busy || !canRun(row)}
                                                checked={canRun(row) && selected.includes(row.id)}
                                                onChange={(e) => setSelected((prev) => (e.target.checked
                                                    ? [...prev, row.id]
                                                    : prev.filter((id) => id !== row.id)))}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <strong>{row.company.name || row.fileName}</strong>
                                            <div className={styles.fieldHint}>
                                                {[row.company.number, row.fileName].filter(Boolean).join(' · ')}
                                            </div>
                                            {single && <div className={styles.fieldHint}>{renderMatch(row)}</div>}
                                        </TableCell>
                                        {!single && (
                                            <TableCell>
                                                <div className={styles.cellFieldWide}>
                                                    <XtramSelect
                                                        direction={direction}
                                                        options={clientOptions(row)}
                                                        value={row.client}
                                                        onChange={(client) => changeClient(row, client)}
                                                        getOptionLabel={clientLabel}
                                                        isOptionEqualToValue={(x, y) => x.caseId === y.caseId}
                                                        filterOptions={filterClients}
                                                        placeholder={tb('chooseClient')}
                                                        disabled={busy}
                                                        error={!row.client}
                                                    />
                                                </div>
                                                <div className={styles.fieldHint}>{renderMatch(row)}</div>
                                            </TableCell>
                                        )}
                                        <TableCell>{renderBookkeeping(row)}</TableCell>
                                        <TableCell>
                                            <div className={styles.cellFieldNarrow}>
                                                <XtramSelect
                                                    direction={direction}
                                                    options={yearOptions}
                                                    value={yearOptions.find((o) => o.value === row.year) ?? null}
                                                    onChange={(o) => o && updateRow(row.id, { year: o.value, comparison: null, status: 'idle' })}
                                                    disabled={busy || yearOptions.length === 0}
                                                    error={row.year == null}
                                                />
                                            </div>
                                            {rowCount > 0 && <div className={styles.fieldHint}>{tb('rowsInYear', { count: rowCount })}</div>}
                                        </TableCell>
                                        <TableCell>
                                            <div className={styles.cellField}>
                                                <XtramSelect
                                                    direction={direction}
                                                    options={actionOptions}
                                                    value={actionOptions.find((o) => o.value === row.action) ?? null}
                                                    onChange={(o) => o && updateRow(row.id, {
                                                        action: o.value,
                                                        ...(row.comparison?.simulated ? { comparison: null, status: 'idle' } : {}),
                                                    })}
                                                    disabled={busy}
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell>{renderStatus(row)}</TableCell>
                                        <TableCell>{renderComparison(row)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </XtramTable>
            </div>
        );
    };

    const renderMissing = () => {
        if (missing.length === 0) return <XtramNotice type="success">{t('NoMissingSortCodes')}</XtramNotice>;
        const bulkField = (field, options, placeholder) => (
            <XtramField size={field === 'vat' ? 'narrow' : undefined}>
                <XtramSelect
                    direction={direction}
                    options={options}
                    placeholder={placeholder}
                    value={field in bulk ? options.find((o) => o.value === bulk[field]) ?? null : null}
                    onChange={(v) => setBulk((prev) => ({ ...prev, [field]: v?.value ?? null }))}
                />
            </XtramField>
        );
        const rowField = (sc, index, field, options, placeholder, required = true) => (
            <div className={styles.cellField}>
                <XtramSelect
                    direction={direction}
                    options={options}
                    placeholder={placeholder}
                    value={options.find((o) => o.value === sc[field]) ?? null}
                    onChange={(v) => updateMissing(index, field, v?.value ?? null)}
                    error={required && sc[field] == null}
                />
            </div>
        );
        return (
            <div className={styles.section}>
                <p className={styles.muted}>{tb('missingHelp')}</p>
                <XtramNotice type="info">
                    <span>{tb('createSortCodesOnlyHelp')}</span>
                    <div>
                        <XtramButton variant="secondary" small disabled={busy || missingIncomplete} onClick={createSortCodesOnly}>
                            {tb('createSortCodesOnly')}
                        </XtramButton>
                    </div>
                </XtramNotice>
                <div className={styles.formFields}>
                    {bulkField('sortCodeVatType', vatTypeOptions, t('SelectSortCodeVatType'))}
                    {bulkField('sortCodeCategory', groupOptions, t('SelectSortCodeGroupName'))}
                    {bulkField('participatesInProfitAndLoss', yesNoOptions, t('participatesInProfitAndLoss'))}
                    {bulkField('vat', vatOptions, t('VAT'))}
                    <XtramButton
                        variant="ghost"
                        disabled={selectedMissing.length === 0 || Object.keys(bulk).length === 0}
                        onClick={applyBulk}
                    >
                        {t('ApplyToSelected')}
                    </XtramButton>
                </div>
                <XtramTable height="tall">
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        className={styles.tableCheckbox}
                                        checked={missing.every((sc) => selectedMissing.includes(sc.sortCodeId))}
                                        onChange={(e) => setSelectedMissing(e.target.checked ? missing.map((sc) => sc.sortCodeId) : [])}
                                    />
                                </TableCell>
                                <TableCell>{t('SortCode Id')}</TableCell>
                                <TableCell>{t('SortCode Name')}</TableCell>
                                <TableCell>{t('SelectSortCodeVatType')}</TableCell>
                                <TableCell>{t('SelectSortCodeGroupName')}</TableCell>
                                <TableCell>{t('participatesInProfitAndLoss')}</TableCell>
                                <TableCell>{t('VAT')}</TableCell>
                                <TableCell>{tb('usedBy')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {relevantMissing.map(({ sc, index }) => (
                                <TableRow key={sc.sortCodeId}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            className={styles.tableCheckbox}
                                            checked={selectedMissing.includes(sc.sortCodeId)}
                                            onChange={(e) => setSelectedMissing((prev) => (e.target.checked
                                                ? [...prev, sc.sortCodeId]
                                                : prev.filter((id) => id !== sc.sortCodeId)))}
                                        />
                                    </TableCell>
                                    <TableCell>{sc.sortCodeId}</TableCell>
                                    <TableCell>
                                        <div className={styles.cellFieldWide}>
                                            <XtramInput
                                                direction={direction}
                                                value={sc.sortCodeName}
                                                onChange={(e) => updateMissing(index, 'sortCodeName', e.target.value)}
                                                error={!sc.sortCodeName?.trim()}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell>{rowField(sc, index, 'sortCodeVatType', vatTypeOptions, t('SelectSortCodeVatType'))}</TableCell>
                                    <TableCell>{rowField(sc, index, 'sortCodeCategory', groupOptions, t('SelectSortCodeGroupName'), false)}</TableCell>
                                    <TableCell>{rowField(sc, index, 'participatesInProfitAndLoss', yesNoOptions, t('participatesInProfitAndLoss'))}</TableCell>
                                    <TableCell>{rowField(sc, index, 'vat', vatOptions, t('VAT'))}</TableCell>
                                    {usedByCell(sc.rowIds)}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </XtramTable>
            </div>
        );
    };

    const usedByCell = (rowIds) => (
        <TableCell>
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{rowIds.map((id) => rowNames.get(id)).join('\n')}</span>}>
                <span>{rowIds.length === 1 ? rowNames.get(rowIds[0]) : tb('businessesCount', { count: rowIds.length })}</span>
            </Tooltip>
        </TableCell>
    );

    const renderPalCodes = () => {
        if (missingPalCodes.length === 0) return <XtramNotice type="success">{tb('noMissingPalCodes')}</XtramNotice>;
        const bulkField = (field, options, placeholder) => (
            <XtramField>
                <XtramSelect
                    direction={direction}
                    options={options}
                    placeholder={placeholder}
                    value={field in palBulk ? options.find((o) => o.value === palBulk[field]) ?? null : null}
                    onChange={(v) => setPalBulk((prev) => ({ ...prev, [field]: v?.value ?? null }))}
                />
            </XtramField>
        );
        return (
            <div className={styles.section}>
                <p className={styles.muted}>{tb('palCodesHelp')}</p>
                <div className={styles.formFields}>
                    {bulkField('palCodeType', palCodeTypeOptions, t('PalCodeType'))}
                    {bulkField('palCodeCategory', palCategoryOptions, t('PalCodeCategory'))}
                    <XtramButton
                        variant="ghost"
                        disabled={selectedPalCodes.length === 0 || Object.keys(palBulk).length === 0}
                        onClick={applyPalBulk}
                    >
                        {t('ApplyToSelected')}
                    </XtramButton>
                </div>
                <XtramTable height="tall">
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        className={styles.tableCheckbox}
                                        checked={missingPalCodes.every((pc) => selectedPalCodes.includes(pc.palCodeName))}
                                        onChange={(e) => setSelectedPalCodes(e.target.checked ? missingPalCodes.map((pc) => pc.palCodeName) : [])}
                                    />
                                </TableCell>
                                <TableCell>{tb('palCodeName')}</TableCell>
                                <TableCell>{t('PalCodeType')}</TableCell>
                                <TableCell>{t('PalCodeCategory')}</TableCell>
                                <TableCell>{tb('usedBy')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {missingPalCodes.map((pc, index) => (
                                <TableRow key={pc.palCodeName}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            className={styles.tableCheckbox}
                                            checked={selectedPalCodes.includes(pc.palCodeName)}
                                            onChange={(e) => setSelectedPalCodes((prev) => (e.target.checked
                                                ? [...prev, pc.palCodeName]
                                                : prev.filter((name) => name !== pc.palCodeName)))}
                                        />
                                    </TableCell>
                                    <TableCell>{pc.palCodeName}</TableCell>
                                    <TableCell>
                                        <div className={styles.cellField}>
                                            <XtramSelect
                                                direction={direction}
                                                options={palCodeTypeOptions}
                                                placeholder={t('PalCodeType')}
                                                value={palCodeTypeOptions.find((o) => o.value === pc.palCodeType) ?? null}
                                                onChange={(v) => updatePalCode(index, 'palCodeType', v?.value ?? null)}
                                                error={!pc.palCodeType}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className={styles.cellField}>
                                            <XtramSelect
                                                direction={direction}
                                                options={palCategoryOptions}
                                                placeholder={t('PalCodeCategory')}
                                                value={palCategoryOptions.find((o) => o.value === pc.palCodeCategory) ?? null}
                                                onChange={(v) => updatePalCode(index, 'palCodeCategory', v?.value ?? null)}
                                            />
                                        </div>
                                    </TableCell>
                                    {usedByCell(pc.rowIds)}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </XtramTable>
            </div>
        );
    };

    const renderCards = () => (
        <div className={styles.section}>
            <XtramNotice type="info">
                <span>{tb('cardsCreatedHelp')}</span>
                {!withCards && (
                    <XtramCheckbox checked={withCards} onChange={setWithCards} label={tb('withCards')} />
                )}
            </XtramNotice>
            <XtramTable height="tall">
                <Table size="small" stickyHeader dir={direction}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('CardId')}</TableCell>
                            <TableCell>{t('CardName')}</TableCell>
                            <TableCell>{t('PalCode')}</TableCell>
                            <TableCell>{t('CompanyId')}</TableCell>
                            <TableCell>{t('Phone')}</TableCell>
                            <TableCell>{t('Address')}</TableCell>
                            <TableCell>{tb('usedBy')}</TableCell>
                            <TableCell>{tb('fileStatus')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {missingCards.map((card) => (
                            <TableRow key={card.cardId}>
                                <TableCell>{card.cardId}</TableCell>
                                <TableCell>{card.cardName || '—'}</TableCell>
                                <TableCell>{card.palCode || '—'}</TableCell>
                                <TableCell>{card.accountCaseId || '—'}</TableCell>
                                <TableCell>{[card.phoneNumber, card.phoneNumber2].filter(Boolean).join(', ') || '—'}</TableCell>
                                <TableCell>{card.location || '—'}</TableCell>
                                {usedByCell(card.rowIds)}
                                <TableCell>
                                    {cardComesWithImport(card)
                                        ? <XtramBadge tone="success">{tb('cardCreatedFromBackup')}</XtramBadge>
                                        : <XtramBadge tone="info">{tb('cardCreatedNew')}</XtramBadge>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </XtramTable>
        </div>
    );

    const renderDetail = () => {
        const options = compared.map((r) => ({
            value: r.id,
            label: `${r.company.name || r.fileName} ← ${clientLabel(r.client)} · ${r.comparison.year}`,
        }));
        return (
            <div className={styles.section}>
                {options.length > 1 && (
                    <div className={styles.formFields}>
                        <XtramField label={tb('business')} size="full">
                            <XtramSelect
                                direction={direction}
                                options={options}
                                value={options.find((o) => o.value === detail?.id) ?? null}
                                onChange={(o) => o && setDetailId(o.value)}
                                placeholder={tb('chooseBusiness')}
                            />
                        </XtramField>
                    </div>
                )}
                {detail?.comparison
                    ? <RivhitBackupComparison key={detail.id} comparison={detail.comparison} direction={direction} />
                    : <XtramNotice type="info">{tb('chooseBusiness')}</XtramNotice>}
            </div>
        );
    };

    useEffect(() => {
        if (tab === 'comparison' && !detail && compared.length > 0) setDetailId(compared[0].id);
    }, [tab, detail, compared]);

    /** A simulation made before the sort codes it adds were set otherwise is made again when it is looked at. */
    const staleIds = compared
        .filter((r) => r.comparison.simulated && simulationOf(r) && (r.simulatedWith ?? '[]') !== JSON.stringify(settingsOf(r)))
        .map((r) => r.id);
    const staleKey = staleIds.join('|');
    useEffect(() => {
        if (tab !== 'comparison' || busy || !staleKey) return;
        setBusy(true);
        compareRows(rowsRef.current.filter((r) => staleIds.includes(r.id)), { keepAction: true })
            .finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, busy, staleKey]);

    const tabs = [
        { key: 'files', label: tb('filesTab'), count: files.length || null },
        { key: 'businesses', label: tb('businessesTab'), count: rows.length || null, disabled: rows.length === 0 },
        ...(missingPalCodes.length > 0 ? [{
            key: 'palCodes',
            label: t('MissingPalCodes'),
            count: missingPalCodes.length,
            alert: palCodesIncomplete,
        }] : []),
        ...(missing.length > 0 ? [{
            key: 'missing',
            label: t('MissingSortCodes'),
            count: missing.length,
            alert: missingIncomplete,
        }] : []),
        ...(missingCards.length > 0 ? [{
            key: 'cards',
            label: t('MissingAccountCards'),
            count: missingCards.length,
        }] : []),
        {
            key: 'comparison',
            label: tb('comparisonTab'),
            count: compared.length || null,
            alert: different.length > 0,
            disabled: compared.length === 0,
        },
    ];

    const importBlocked = (tab === 'missing' && missingIncomplete)
        || (tab === 'palCodes' && palCodesIncomplete);
    const createsMissing = missing.length > 0 || missingPalCodes.length > 0 || missingCards.length > 0;

    const progressLabel = progress
        ? tb(progress.checking ? 'checking' : 'progress', { done: progress.done + 1, total: progress.total, name: progress.name })
        : null;

    return (
        <XtramModal
            open={!!open}
            onClose={handleClose}
            direction={direction}
            busy={busy}
            expanded={rows.length > 0}
            title={single ? tb('title', { client: clientName || caseId }) : tb('titleAll')}
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            footer={(
                <>
                    {progressLabel
                        ? <span className={styles.footerInfo}>{progressLabel}</span>
                        : rows.length > 0 && <span className={styles.footerInfo}>{tb('selectedCount', { count: chosen.length })}</span>}
                    <XtramButton variant="secondary" onClick={handleClose} disabled={busy}>
                        {t('cancelButton')}
                    </XtramButton>
                    {tab === 'files' ? (
                        <XtramButton variant="primary" onClick={readFiles} disabled={busy || pendingFiles === 0} busy={busy}>
                            {tb('read', { count: pendingFiles })}
                        </XtramButton>
                    ) : (
                        <>
                            <XtramButton variant="ghost" onClick={runCompare} disabled={busy || chosen.length === 0}>
                                {tb('compareSelected')}
                            </XtramButton>
                            <XtramButton
                                variant="primary"
                                onClick={runImport}
                                disabled={busy || chosen.length === 0 || importBlocked}
                                busy={busy}
                            >
                                {createsMissing ? tb('createAndImport') : tb('importSelected')}
                            </XtramButton>
                        </>
                    )}
                </>
            )}
        >
            {tab === 'files' && renderFiles()}
            {tab === 'businesses' && renderBusinesses()}
            {tab === 'palCodes' && renderPalCodes()}
            {tab === 'missing' && renderMissing()}
            {tab === 'cards' && renderCards()}
            {tab === 'comparison' && renderDetail()}
        </XtramModal>
    );
};

export default RivhitBackupImportModal;
